import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Clock, MapPin, ShieldCheck } from "lucide-react";
import { PACKAGES, STUDIO } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build package options with duration for slot logic
const buildPackageOptions = () => {
  const opts = [];
  PACKAGES.forEach((pkg) => {
    pkg.rows.forEach((row) => {
      const price = Number(row.price.replace(/[^0-9.]/g, ""));
      // Parse duration: "1 hour", "2 hours", "3½ hours"
      let hrs = 1.0;
      const raw = row.duration || "";
      if (raw.includes("½")) hrs = parseFloat(raw) + 0.5;
      else hrs = parseFloat(raw) || 1;
      opts.push({
        id: `${pkg.name}-${row.location}`.toLowerCase().replace(/\s+/g, "-"),
        label: `${pkg.name} · ${row.location}`,
        location: row.location,
        pkgName: pkg.name,
        durationLabel: row.duration,
        durationHours: hrs,
        price,
      });
    });
  });
  return opts;
};

const formatDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const CLOSED_DAYS = [0, 1]; // Sunday & Monday closed

export default function Book() {
  const navigate = useNavigate();
  const packageOptions = useMemo(buildPackageOptions, []);
  const [pkg, setPkg] = useState(packageOptions[2]); // Standard Studio default
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState(null); // { open, slots }
  const [slot, setSlot] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch availability whenever date or package changes
  useEffect(() => {
    if (!selectedDate) {
      setAvailability(null);
      return;
    }
    setLoadingAvail(true);
    setSlot("");
    axios
      .get(`${API}/bookings/availability`, {
        params: {
          date_str: formatDateISO(selectedDate),
          duration_hours: pkg.durationHours,
        },
      })
      .then((r) => setAvailability(r.data))
      .catch(() => toast.error("Couldn't load availability. Please retry."))
      .finally(() => setLoadingAvail(false));
  }, [selectedDate, pkg]);

  const disabled = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (CLOSED_DAYS.includes(d.getDay())) return true;
    return false;
  };

  const canContinue = pkg && selectedDate && slot && name && email;

  const submit = async () => {
    if (!canContinue) {
      toast.error("Please pick a date, a time, and share your details.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        date: formatDateISO(selectedDate),
        start_time: slot,
        duration_hours: pkg.durationHours,
        package: pkg.label,
        location: pkg.location,
        package_price: pkg.price,
        customer_name: name,
        customer_email: email,
        customer_phone: phone || null,
        notes: notes || null,
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      toast.success("Date reserved. One more step — secure it with a deposit.");
      navigate(`/pay?booking_id=${data.id}`);
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Could not create booking. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Booking failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="book-page" className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <Link
          to="/"
          data-testid="book-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#4A4A4A] hover:text-[#C5A059] transition-colors"
        >
          <ArrowLeft size={14} /> Back to lsb-studio
        </Link>

        <p className="overline mt-10">Reserve your date</p>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-4">
          Book your session,
          <br />
          <span className="italic font-light">step by step.</span>
        </h1>
        <p className="text-[#4A4A4A] mt-6 max-w-xl leading-relaxed">
          Pick your package, then choose a date and time from our live
          availability. A 50% deposit locks the date; balance is due day-of.
        </p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left column — steps */}
          <div className="lg:col-span-2 space-y-14">
            {/* 1. Package */}
            <section>
              <p className="overline mb-5">1 · Choose a package</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {packageOptions.map((o) => (
                  <button
                    key={o.id}
                    data-testid={`book-pkg-${o.id}`}
                    onClick={() => setPkg(o)}
                    className={`text-left p-4 border transition-all ${
                      pkg.id === o.id
                        ? "border-[#C5A059] bg-[#F2EFE9]"
                        : "border-black/10 hover:border-black/30"
                    }`}
                  >
                    <p className="font-serif-display text-lg">{o.label}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[#4A4A4A]">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} /> {o.durationLabel}
                      </span>
                      <span>·</span>
                      <span>${o.price.toFixed(0)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {/* 2. Date */}
            <section>
              <p className="overline mb-5">2 · Pick a date</p>
              <div className="inline-block border border-black/10 bg-white p-2 rounded-sm">
                <Calendar
                  data-testid="book-calendar"
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabled}
                  fromDate={new Date()}
                />
              </div>
              <p className="text-xs text-[#4A4A4A] mt-3">
                Studio hours: Tue–Sun, 10:00–18:00. Closed Mondays.
              </p>
            </section>

            {/* 3. Time */}
            <section>
              <p className="overline mb-5">3 · Choose a time</p>
              {!selectedDate ? (
                <p className="text-sm text-[#4A4A4A]">
                  Pick a date to see available times.
                </p>
              ) : loadingAvail ? (
                <p className="text-sm text-[#4A4A4A]">Loading availability…</p>
              ) : !availability?.open ? (
                <p className="text-sm text-[#4A4A4A]">
                  Studio is closed on that date. Please pick another.
                </p>
              ) : availability.slots.length === 0 ? (
                <p className="text-sm text-[#4A4A4A]">
                  No open slots that day. Please pick another date.
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {availability.slots.map((s) => (
                    <button
                      key={s}
                      data-testid={`book-slot-${s}`}
                      onClick={() => setSlot(s)}
                      className={`py-3 text-sm border font-serif-display transition-colors ${
                        slot === s
                          ? "border-[#C5A059] bg-[#1A1A1A] text-white"
                          : "border-black/10 hover:border-black/30"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* 4. Details */}
            <section>
              <p className="overline mb-5">4 · Your details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label className="overline">Full name *</label>
                  <input
                    data-testid="book-name"
                    className="line-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="overline">Email *</label>
                  <input
                    data-testid="book-email"
                    type="email"
                    className="line-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="overline">Phone</label>
                  <input
                    data-testid="book-phone"
                    type="tel"
                    className="line-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div className="sm:col-span-2 mt-2">
                  <label className="overline">Notes for the studio</label>
                  <textarea
                    data-testid="book-notes"
                    rows={3}
                    className="line-input resize-none"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything we should know? Location ideas, wardrobe, group size…"
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Right — sticky summary */}
          <aside className="lg:col-span-1">
            <div className="bg-[#F2EFE9] p-6 md:p-8 sticky top-28">
              <p className="overline">Your session</p>
              <div className="mt-4 space-y-3 pb-5 border-b border-black/10 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#4A4A4A]">Package</span>
                  <span className="font-serif-display text-right">
                    {pkg.label}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4A4A4A]">Duration</span>
                  <span className="font-serif-display">
                    {pkg.durationLabel}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4A4A4A]">Date</span>
                  <span className="font-serif-display">
                    {selectedDate
                      ? selectedDate.toLocaleDateString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4A4A4A]">Time</span>
                  <span className="font-serif-display">{slot || "—"}</span>
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#4A4A4A]">Session price</span>
                  <span className="font-serif-display">
                    ${pkg.price.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="overline">Deposit today (50%)</span>
                  <span
                    data-testid="book-deposit-total"
                    className="font-serif-display text-3xl"
                  >
                    ${(pkg.price / 2).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-[#4A4A4A] pt-2">
                  Balance ${(pkg.price / 2).toFixed(2)} due at session.
                </p>
              </div>

              <button
                data-testid="book-continue-btn"
                onClick={submit}
                disabled={!canContinue || submitting}
                className="mt-8 w-full py-4 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.28em] hover:bg-[#C5A059] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Reserving…" : "Continue to Deposit"}
              </button>

              <div className="mt-5 flex items-start gap-2 text-xs text-[#4A4A4A]">
                <ShieldCheck className="w-4 h-4 text-[#C5A059] flex-shrink-0 mt-0.5" />
                <p>
                  We hold your slot for 15 minutes while you pay. Payments are
                  processed securely by PayPal.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
